import { cardSchema } from '@blueprint/domain'
import type {
  Actor,
  FeedbackPageKey,
  IntegrationScope,
  BoardPage,
  BoardPageRequest,
  BoardSnapshot,
  Card,
  MindmapLinks,
  MindmapNode,
  MindmapSave,
  MindmapView,
  OperationBatch,
  ShareRole,
  Tenant,
  User,
  WorkflowLinks,
  WorkflowSave,
  WorkflowView,
  ProjectLabels,
  ProjectMember,
  ProjectSummary,
} from '@blueprint/domain'
import {
  RepositoryError,
  type AccountsApps,
  type AuthConfig,
  type BoardRepository,
  type BoardShareEntry,
  type BoardVersion,
  type CardVersion,
  type FeedbackInput,
  type IntegrationToken,
  type MutationAck,
  type NewUserInput,
  type SignedIn,
  type UserChangesInput,
} from './repository.js'

/** Same-origin in development: Vite proxies /api to the Fastify service. */
const BASE = '/api'

interface WireError {
  error?: {
    code?: unknown
    message?: unknown
    requestId?: unknown
    details?: unknown
    currentRevision?: unknown
  }
}

const KINDS = {
  validation: 'validation',
  not_found: 'not_found',
  conflict: 'conflict',
  server: 'server',
} as const

/** Maps the wire error onto the kind the save queue and conflict UI already understand. */
function toRepositoryError(status: number, body: unknown): RepositoryError {
  const wire = (body ?? {}) as WireError
  const error = wire.error ?? {}
  const code = typeof error.code === 'string' ? error.code : ''
  const kind =
    code in KINDS
      ? KINDS[code as keyof typeof KINDS]
      : status === 401
        ? 'unauthorized'
        : status === 403
          ? 'forbidden'
          : status === 404
            ? 'not_found'
            : status === 409
              ? 'conflict'
              : status >= 400 && status < 500
                ? 'validation'
                : 'server'

  const message =
    typeof error.message === 'string' && error.message.length > 0
      ? error.message
      : 'The server could not complete that request.'

  return new RepositoryError(kind, message, {
    currentRevision: typeof error.currentRevision === 'number' ? error.currentRevision : undefined,
    details: isStringRecord(error.details) ? error.details : undefined,
    requestId: typeof error.requestId === 'string' ? error.requestId : undefined,
  })
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return typeof value === 'object' && value !== null && Object.values(value).every((entry) => typeof entry === 'string')
}

/**
 * The storage adapter. Every board read and write goes through the API to the database.
 *
 * A failure to reach the server is reported as `network`, which the save queue treats as
 * retryable with the identical mutation id — so a lost response cannot lose work or
 * create a duplicate. Everything else is decided by the server's status code.
 */
export class ApiBoardRepository implements BoardRepository {
  readonly mode = 'api' as const
  /** The API has one storage, PostgreSQL. */
  readonly durability = 'Saved in Blueprint — your changes are kept when you refresh.'

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    let response: Response
    try {
      response = await fetch(`${BASE}${path}`, {
        ...init,
        // The session is an httpOnly cookie: it only travels if asked for explicitly.
        credentials: 'include',
        headers: init?.body ? { 'content-type': 'application/json', ...init?.headers } : init?.headers,
      })
    } catch (error) {
      // Offline, DNS, refused connection: keep the optimistic change and allow a retry.
      throw new RepositoryError('network', 'The server could not be reached.', {
        details: { cause: error instanceof Error ? error.message : 'unknown' },
      })
    }

    if (response.status === 204) return undefined as T

    let body: unknown = null
    const text = await response.text()
    if (text.length > 0) {
      try {
        body = JSON.parse(text)
      } catch {
        if (response.ok) {
          throw new RepositoryError('server', 'The server sent a response that could not be read.')
        }
      }
    }

    if (!response.ok) throw toRepositoryError(response.status, body)
    return body as T
  }

  // --- identity -----------------------------------------------------------------

  /**
   * Null rather than an error when there is no session: "nobody is signed in" is an
   * answer, not a failure, and it is what the app asks on every load.
   */
  async currentUser(): Promise<SignedIn | null> {
    try {
      const body = await this.request<{ user: Actor; trial?: boolean; backToAccountsUrl?: string | null }>('/auth/me')
      return { user: body.user, trial: body.trial === true, backToAccountsUrl: body.backToAccountsUrl ?? null }
    } catch (error) {
      if (error instanceof RepositoryError && error.kind === 'unauthorized') return null
      throw error
    }
  }

  async authConfig(): Promise<AuthConfig> {
    return this.request<AuthConfig>('/auth/config')
  }

  async logOut(): Promise<string | null> {
    // 204 with Teamcentric Accounts off; with it on, where to sign out of teamcentric too.
    const body = await this.request<{ logoutUrl?: string } | undefined>('/auth/logout', { method: 'POST' })
    return body?.logoutUrl ?? null
  }

  async accountsApps(): Promise<AccountsApps> {
    return this.request<AccountsApps>('/auth/apps')
  }

  async updateProfile(displayName: string): Promise<Actor> {
    const body = await this.request<{ user: Actor }>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    })
    return body.user
  }

  async listIntegrationTokens(): Promise<IntegrationToken[]> {
    const body = await this.request<{ tokens: IntegrationToken[] }>('/integrations/tokens')
    return body.tokens
  }

  async createIntegrationToken(input: { name: string; scopes: IntegrationScope[]; permissions?: IntegrationToken['permissions']; expiresInDays?: number }): Promise<{ token: IntegrationToken; secret: string }> {
    return this.request('/integrations/tokens', { method: 'POST', body: JSON.stringify(input) })
  }

  async revokeIntegrationToken(id: string): Promise<void> {
    await this.request<void>(`/integrations/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' })
  }

  // --- users --------------------------------------------------------------------

  async listUsers(): Promise<User[]> {
    return this.request<User[]>('/users')
  }

  async createUser(input: NewUserInput): Promise<User & { invitationSent?: boolean }> {
    return this.request<User & { invitationSent?: boolean }>('/users', { method: 'POST', body: JSON.stringify(input) })
  }

  async reinviteUser(id: string): Promise<boolean> {
    const body = await this.request<{ invitationSent: boolean }>(`/users/${encodeURIComponent(id)}/invitation`, { method: 'POST' })
    return body.invitationSent
  }

  async updateUser(id: string, changes: UserChangesInput): Promise<User> {
    return this.request<User>(`/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    })
  }

  async getTenant(): Promise<Tenant> {
    return this.request<Tenant>('/tenant')
  }

  async setTenantMcpEnabled(enabled: boolean): Promise<Tenant> {
    return this.request<Tenant>('/tenant/mcp', { method: 'POST', body: JSON.stringify({ enabled }) })
  }

  async billingAvailable(): Promise<boolean> {
    return (await this.request<{ available: boolean }>('/billing')).available
  }

  async billingHandoff(): Promise<string> {
    return (await this.request<{ url: string }>('/billing/handoff', { method: 'POST' })).url
  }

  // --- feedback -----------------------------------------------------------------

  async feedbackQuestion(pageKey: FeedbackPageKey): Promise<string> {
    return (await this.request<{ question: string }>(`/feedback/prompt?pageKey=${encodeURIComponent(pageKey)}`)).question
  }

  async sendFeedback(input: FeedbackInput): Promise<void> {
    await this.request<{ id: string }>('/feedback', { method: 'POST', body: JSON.stringify(input) })
  }

  // --- sharing ------------------------------------------------------------------

  async listShares(boardId: string): Promise<BoardShareEntry[]> {
    return this.request<BoardShareEntry[]>(`/boards/${encodeURIComponent(boardId)}/shares`)
  }

  async shareBoard(boardId: string, userId: string, role: ShareRole): Promise<BoardShareEntry[]> {
    return this.request<BoardShareEntry[]>(`/boards/${encodeURIComponent(boardId)}/shares`, {
      method: 'PUT',
      body: JSON.stringify({ userId, role }),
    })
  }

  async unshareBoard(boardId: string, userId: string): Promise<BoardShareEntry[]> {
    return this.request<BoardShareEntry[]>(
      `/boards/${encodeURIComponent(boardId)}/shares/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    )
  }

  async reassignOwner(boardId: string, newOwnerId: string): Promise<void> {
    await this.request<void>(`/boards/${encodeURIComponent(boardId)}/owner`, {
      method: 'POST',
      body: JSON.stringify({ ownerId: newOwnerId }),
    })
  }

  async listBoards(page: BoardPageRequest = {}): Promise<BoardPage> {
    const query = new URLSearchParams()
    if (page.limit !== undefined) query.set('limit', String(page.limit))
    if (page.offset !== undefined) query.set('offset', String(page.offset))
    if (page.kind !== undefined) query.set('kind', page.kind)
    if (page.projectId !== undefined) query.set('project', page.projectId)
    if (page.shared) query.set('shared', 'true')
    const suffix = query.size > 0 ? `?${query.toString()}` : ''
    return this.request<BoardPage>(`/boards${suffix}`)
  }

  async createBoard(input: { id: string; name: string; projectId?: string }): Promise<BoardSnapshot> {
    return this.request<BoardSnapshot>('/boards', { method: 'POST', body: JSON.stringify(input) })
  }

  async getBoard(id: string): Promise<BoardSnapshot> {
    return this.request<BoardSnapshot>(`/boards/${encodeURIComponent(id)}`)
  }

  async getBoardRevision(id: string): Promise<number> {
    const response = await this.request<{ revision: number }>(`/boards/${encodeURIComponent(id)}/revision`)
    return response.revision
  }

  async listBoardVersions(id: string): Promise<BoardVersion[]> {
    const response = await this.request<{ versions: BoardVersion[] }>(`/boards/${encodeURIComponent(id)}/versions`)
    return response.versions
  }

  async getBoardVersion(id: string, revision: number): Promise<BoardSnapshot> {
    return this.request<BoardSnapshot>(`/boards/${encodeURIComponent(id)}/versions/${revision}`)
  }

  async listCardVersions(boardId: string, cardId: string): Promise<CardVersion[]> {
    const response = await this.request<{ versions: CardVersion[] }>(
      `/boards/${encodeURIComponent(boardId)}/cards/${encodeURIComponent(cardId)}/versions`,
    )
    return response.versions
  }

  /** Parsed, so a version stored before a field existed arrives with that field's defaults. */
  async getCardVersion(boardId: string, cardId: string, version: number): Promise<Card> {
    const card = await this.request<unknown>(
      `/boards/${encodeURIComponent(boardId)}/cards/${encodeURIComponent(cardId)}/versions/${version}`,
    )
    return cardSchema.parse(card)
  }

  async deleteBoard(id: string, expectedRevision: number): Promise<void> {
    await this.request<void>(
      `/boards/${encodeURIComponent(id)}?expectedRevision=${encodeURIComponent(String(expectedRevision))}`,
      { method: 'DELETE' },
    )
  }

  async importBoard(snapshot: BoardSnapshot, projectId?: string): Promise<BoardSnapshot> {
    const suffix = projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''
    return this.request<BoardSnapshot>(`/boards/import${suffix}`, { method: 'POST', body: JSON.stringify(snapshot) })
  }

  async applyOperations(boardId: string, batch: OperationBatch): Promise<MutationAck> {
    return this.request<MutationAck>(`/boards/${encodeURIComponent(boardId)}/operations`, {
      method: 'POST',
      body: JSON.stringify(batch),
    })
  }

  async createMindmap(input: { id: string; name: string; root?: MindmapNode; projectId?: string }): Promise<MindmapView> {
    return this.request<MindmapView>('/mindmaps', { method: 'POST', body: JSON.stringify(input) })
  }

  async getMindmap(id: string): Promise<MindmapView> {
    return this.request<MindmapView>(`/mindmaps/${encodeURIComponent(id)}`)
  }

  async saveMindmap(id: string, save: MindmapSave): Promise<MindmapView> {
    return this.request<MindmapView>(`/mindmaps/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(save) })
  }

  async listMindmapLinks(id: string): Promise<MindmapLinks> {
    return this.request<MindmapLinks>(`/mindmaps/${encodeURIComponent(id)}/links`)
  }

  async createWorkflow(input: { id: string; name: string; projectId?: string }): Promise<WorkflowView> {
    return this.request<WorkflowView>('/workflows', { method: 'POST', body: JSON.stringify(input) })
  }

  async getWorkflow(id: string): Promise<WorkflowView> {
    return this.request<WorkflowView>(`/workflows/${encodeURIComponent(id)}`)
  }

  async saveWorkflow(id: string, save: WorkflowSave): Promise<WorkflowView> {
    return this.request<WorkflowView>(`/workflows/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(save) })
  }

  async listWorkflowLinks(id: string): Promise<WorkflowLinks> {
    return this.request<WorkflowLinks>(`/workflows/${encodeURIComponent(id)}/links`)
  }

  async listProjects(): Promise<ProjectSummary[]> {
    return (await this.request<{ projects: ProjectSummary[] }>('/projects')).projects
  }

  async createProject(input: { id: string; name: string }): Promise<ProjectSummary> {
    return this.request<ProjectSummary>('/projects', { method: 'POST', body: JSON.stringify(input) })
  }

  async renameProject(id: string, name: string): Promise<ProjectSummary> {
    return this.request<ProjectSummary>(`/projects/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ name }) })
  }

  async deleteProject(id: string): Promise<void> {
    await this.request<void>(`/projects/${encodeURIComponent(id)}`, { method: 'DELETE' })
  }

  async listProjectMembers(id: string): Promise<ProjectMember[]> {
    return this.request<ProjectMember[]>(`/projects/${encodeURIComponent(id)}/members`)
  }

  async setProjectMember(id: string, userId: string, role: ShareRole): Promise<ProjectMember[]> {
    return this.request<ProjectMember[]>(`/projects/${encodeURIComponent(id)}/members`, { method: 'PUT', body: JSON.stringify({ userId, role }) })
  }

  async removeProjectMember(id: string, userId: string): Promise<ProjectMember[]> {
    return this.request<ProjectMember[]>(`/projects/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, { method: 'DELETE' })
  }

  async moveBoard(boardId: string, projectId: string): Promise<void> {
    await this.request<void>(`/boards/${encodeURIComponent(boardId)}/project`, { method: 'POST', body: JSON.stringify({ projectId }) })
  }

  async boardProject(boardId: string): Promise<string> {
    return (await this.request<{ projectId: string }>(`/boards/${encodeURIComponent(boardId)}/project`)).projectId
  }

  async projectLabels(boardId: string): Promise<ProjectLabels> {
    return this.request<ProjectLabels>(`/boards/${encodeURIComponent(boardId)}/labels`)
  }
}
